import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Checkbox,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Switch } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// iOS 스타일 토글 스위치
const IOSSwitch = styled(props => <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />)(
  ({ theme }) => ({
    width: 40,
    height: 20,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(20px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#0F7F85',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 16,
      height: 16,
    },
    '& .MuiSwitch-track': {
      borderRadius: 10,
      backgroundColor: '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }),
);

// 시간 선택 셀렉트 스타일
const TimeSelect = styled(Select)({
  width: '123px',
  height: '40px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D9D9D9',
    borderRadius: '6px',
  },
  '& .MuiSelect-select': {
    fontSize: '16px',
    fontWeight: 500,
    color: '#262626',
    padding: '8px 16px',
  },
  '& .MuiSvgIcon-root': {
    color: '#0F7F85',
  },
});

// 체크박스 스타일
const CustomCheckbox = styled(Checkbox)({
  padding: 0,
  '&.MuiCheckbox-root': {
    color: '#D9D9D9',
  },
  '&.Mui-checked': {
    color: '#0F7F85',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
  },
});

const DAY_NAMES = ['월요일', '화요일', '수요일', '목요일', '금요일', '토요일', '일요일'];

const DEFAULT_SCHEDULE = DAY_NAMES.map((_, index) => ({
  dayOfWeek: index + 1,
  isAvailable: true,
  startHour: '09',
  startMinute: '00',
  endHour: '23',
  endMinute: '00',
}));

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '10', '15', '20', '30', '45'];

const BusinessHoursSettingModal = ({
  open,
  onClose,
  title = '영업시간설정',
  initialSchedule = null,
  onConfirm,
}) => {
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [applyWeekdays, setApplyWeekdays] = useState(false);
  const [applyWeekends, setApplyWeekends] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialSchedule && initialSchedule.length > 0) {
        setSchedule(initialSchedule);
      } else {
        setSchedule(DEFAULT_SCHEDULE);
      }
      setApplyWeekdays(false);
      setApplyWeekends(false);
    }
  }, [open, initialSchedule]);

  const handleToggleDay = (index, checked) => {
    const newSchedule = [...schedule];
    newSchedule[index] = { ...newSchedule[index], isAvailable: checked };

    // 평일 일괄 적용
    if (applyWeekdays && index < 5) {
      for (let i = 0; i < 5; i++) {
        newSchedule[i] = { ...newSchedule[i], isAvailable: checked };
      }
    }

    // 주말 일괄 적용
    if (applyWeekends && index >= 5) {
      for (let i = 5; i < 7; i++) {
        newSchedule[i] = { ...newSchedule[i], isAvailable: checked };
      }
    }

    setSchedule(newSchedule);
  };

  const handleTimeChange = (index, field, value) => {
    const newSchedule = [...schedule];
    newSchedule[index] = { ...newSchedule[index], [field]: value };

    // 평일 일괄 적용
    if (applyWeekdays && index < 5) {
      for (let i = 0; i < 5; i++) {
        newSchedule[i] = { ...newSchedule[i], [field]: value };
      }
    }

    // 주말 일괄 적용
    if (applyWeekends && index >= 5) {
      for (let i = 5; i < 7; i++) {
        newSchedule[i] = { ...newSchedule[i], [field]: value };
      }
    }

    setSchedule(newSchedule);
  };

  const handleApplyWeekdaysChange = checked => {
    setApplyWeekdays(checked);
    if (checked) {
      // 월요일 설정을 평일 전체에 적용
      const mondaySettings = schedule[0];
      const newSchedule = [...schedule];
      for (let i = 0; i < 5; i++) {
        newSchedule[i] = { ...mondaySettings, dayOfWeek: i + 1 };
      }
      setSchedule(newSchedule);
    }
  };

  const handleApplyWeekendsChange = checked => {
    setApplyWeekends(checked);
    if (checked) {
      // 토요일 설정을 주말 전체에 적용
      const saturdaySettings = schedule[5];
      const newSchedule = [...schedule];
      for (let i = 5; i < 7; i++) {
        newSchedule[i] = { ...saturdaySettings, dayOfWeek: i + 1 };
      }
      setSchedule(newSchedule);
    }
  };

  // 확정은 onConfirm 만 부른다. 닫기는 부모가 onConfirm 안에서 한다 — onClose 는 "취소" 경로라
  // 확정 직후에 부르면 부모가 아직 갱신 전 상태(커스텀 없음)를 보고 라디오를 지점시간으로 되돌린다.
  const handleConfirm = () => {
    onConfirm(schedule);
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '1000px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    overflow: 'hidden',
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      aria-labelledby="business-hours-modal-title">
      <Box sx={modalStyle}>
        {/* 헤더 */}
        <Box
          sx={{
            height: '56px',
            backgroundColor: '#262626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '32px',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}>
          <Typography
            id="business-hours-modal-title"
            sx={{
              color: '#FFFFFF',
              fontSize: '20px',
              fontWeight: 600,
            }}>
            {title}
          </Typography>
          <CloseIcon
            sx={{ color: '#FFFFFF', width: '32px', height: '32px', cursor: 'pointer' }}
            onClick={onClose}
          />
        </Box>

        {/* 컨텐츠 */}
        <Box sx={{ p: '32px', display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {/* 요일별 설정 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {schedule.map((day, index) => {
              const isWeekday = index < 5;
              const isDisabledByBatch = (isWeekday && applyWeekdays && index !== 0) ||
                                         (!isWeekday && applyWeekends && index !== 5);

              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: '24px',
                    alignItems: 'center',
                    opacity: isDisabledByBatch ? 0.64 : 1,
                  }}>
                  {/* 요일명 */}
                  <Typography
                    sx={{
                      width: '50px',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#262626',
                    }}>
                    {DAY_NAMES[index]}
                  </Typography>

                  {/* 영업 토글 */}
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>
                      영업
                    </Typography>
                    <IOSSwitch
                      checked={day.isAvailable}
                      onChange={e => handleToggleDay(index, e.target.checked)}
                      disabled={isDisabledByBatch}
                    />
                  </Box>

                  {/* 시간 설정 */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      opacity: day.isAvailable ? 1 : 0.5,
                    }}>
                    {/* 개장 시간 */}
                    <TimeSelect
                      value={day.startHour}
                      onChange={e => handleTimeChange(index, 'startHour', e.target.value)}
                      disabled={!day.isAvailable || isDisabledByBatch}
                      IconComponent={KeyboardArrowDownIcon}>
                      {HOURS.map(hour => (
                        <MenuItem key={hour} value={hour}>
                          {hour}
                        </MenuItem>
                      ))}
                    </TimeSelect>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>:</Typography>
                    <TimeSelect
                      value={day.startMinute}
                      onChange={e => handleTimeChange(index, 'startMinute', e.target.value)}
                      disabled={!day.isAvailable || isDisabledByBatch}
                      IconComponent={KeyboardArrowDownIcon}>
                      {MINUTES.map(minute => (
                        <MenuItem key={minute} value={minute}>
                          {minute}
                        </MenuItem>
                      ))}
                    </TimeSelect>

                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626', mx: 1 }}>~</Typography>

                    {/* 폐장 시간 */}
                    <TimeSelect
                      value={day.endHour}
                      onChange={e => handleTimeChange(index, 'endHour', e.target.value)}
                      disabled={!day.isAvailable || isDisabledByBatch}
                      IconComponent={KeyboardArrowDownIcon}>
                      {HOURS.map(hour => (
                        <MenuItem key={hour} value={hour}>
                          {hour}
                        </MenuItem>
                      ))}
                    </TimeSelect>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626' }}>:</Typography>
                    <TimeSelect
                      value={day.endMinute}
                      onChange={e => handleTimeChange(index, 'endMinute', e.target.value)}
                      disabled={!day.isAvailable || isDisabledByBatch}
                      IconComponent={KeyboardArrowDownIcon}>
                      {MINUTES.map(minute => (
                        <MenuItem key={minute} value={minute}>
                          {minute}
                        </MenuItem>
                      ))}
                    </TimeSelect>
                  </Box>

                  {/* 일괄 적용 체크박스 - 월요일, 토요일에만 표시 */}
                  {index === 0 && (
                    <Box
                      sx={{ display: 'flex', gap: '8px', alignItems: 'center', ml: 1, cursor: 'pointer' }}
                      onClick={() => handleApplyWeekdaysChange(!applyWeekdays)}>
                      <CustomCheckbox
                        checked={applyWeekdays}
                        onChange={e => {
                          e.stopPropagation();
                          handleApplyWeekdaysChange(e.target.checked);
                        }}
                      />
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', userSelect: 'none' }}>
                        평일(월~금) 일괄 적용
                      </Typography>
                    </Box>
                  )}
                  {index === 5 && (
                    <Box
                      sx={{ display: 'flex', gap: '8px', alignItems: 'center', ml: 1, cursor: 'pointer' }}
                      onClick={() => handleApplyWeekendsChange(!applyWeekends)}>
                      <CustomCheckbox
                        checked={applyWeekends}
                        onChange={e => {
                          e.stopPropagation();
                          handleApplyWeekendsChange(e.target.checked);
                        }}
                      />
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#262626', userSelect: 'none' }}>
                        주말(토, 일) 일괄 적용
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* 버튼 영역 */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <Button
              onClick={onClose}
              sx={{
                width: '120px',
                height: '48px',
                backgroundColor: '#D9D9D9',
                borderRadius: '8px',
                fontSize: '20px',
                fontWeight: 600,
                color: '#262626',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#BFBFBF',
                },
              }}>
              취소
            </Button>
            <Button
              onClick={handleConfirm}
              sx={{
                width: '240px',
                height: '48px',
                backgroundColor: '#0F7F85',
                borderRadius: '8px',
                fontSize: '20px',
                fontWeight: 600,
                color: '#FFFFFF',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#0D6A6F',
                },
              }}>
              설정완료
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default BusinessHoursSettingModal;
